import React from 'react';
import QRCode from 'react-qr-code';

export interface QRCodeDisplayProps {
  /** The image URL to display */
  imageUrl?: string;
  /** The URL to encode in the QR code */
  value: string;
  /** Size of the QR code in pixels */
  size?: number;
  /** Background color of the QR code */
  bgColor?: string;
  /** Foreground color of the QR code */
  fgColor?: string;
  /** CSS class name for styling */
  className?: string;
  /** Loading state */
  isLoading?: boolean;
  /** Error state */
  hasError?: boolean;
  /** Error message to display */
  errorMessage?: string;
}

/**
 * QRCodeDisplay component renders a QR code for channel URLs
 *
 * Features:
 * - Generates QR code from URL
 * - Loading and error states
 * - Customizable styling
 * - Responsive design
 * - Accessibility support
 */
const QRCodeDisplay: React.FC<QRCodeDisplayProps> = ({
  imageUrl,
  value,
  size = 178,
  bgColor = '#FFFFFF',
  fgColor = '#000000',
  className = '',
}) => {
  // Don't render if no value provided
  if (!value && !imageUrl) {
    return null;
  }

  // Success state - render QR code
  return (
    <div
      className={`sf:p-2 sf:bg-white sf:border sf:border-gray-200 sf:rounded-2xl sf:shadow-lg sf:backdrop-blur-sm ${className}`}
      role="img"
      aria-label={`QR code for ${value}`}
    >
      <div className="sf:flex sf:justify-center">
        <div
          className="sf:bg-gray-50 sf:rounded-xl"
          style={{ width: size, height: size }}
        >
          {imageUrl ? (
            <img src={imageUrl} alt="QR Code" />
          ) : (
            <QRCode
              value={value}
              size={size}
              bgColor={bgColor}
              fgColor={fgColor}
              level="M" // Medium error correction level
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default QRCodeDisplay;
